import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { businessService } from './businessService';

// Types
export interface Message {
  id?: string;
  conversation: {
    conversationId: string;
    businessId: string;
    senderId: string;
    recipientId: string;
    messageType: 'inquiry' | 'response' | 'follow_up' | 'system';
    subject: string;
    content: string;
    // Structured inquiry data
    inquiryData?: {
      name: string;
      email: string;
      phone: string;
      message: string;
      username?: string;
      businessInfo?: {
        title: string;
        url: string;
        askingPrice: number;
        firstImageUrl?: string;
      };
    };
    attachments: string[];
    read: boolean;
    readAt?: Timestamp;
    createdAt: Timestamp;
    updatedAt: Timestamp;
  };
  metadata: {
    priority: 'low' | 'normal' | 'high';
    tags: string[];
    autoReply: boolean;
  };
}

export interface Conversation {
  id?: string;
  participants: string[];
  businessId: string;
  businessTitle?: string;
  lastMessage?: {
    content: string;
    senderId: string;
    timestamp: Timestamp;
  };
  messageCount?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: 'active' | 'archived' | 'closed';
}

// Message Service Class
class MessageService {
  private messagesCollection = 'messages';
  private conversationsCollection = 'conversations';

  // Create a new message
  async createMessage(messageData: Omit<Message, 'id' | 'conversation.createdAt' | 'conversation.updatedAt' | 'conversation.readAt' | 'analytics'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const messageWithDefaults: Omit<Message, 'id'> = {
        ...messageData,
        conversation: {
          ...messageData.conversation,
          createdAt: now,
          updatedAt: now,
          read: false
        },
        metadata: {
          priority: messageData.metadata?.priority || 'normal',
          tags: messageData.metadata?.tags || [],
          autoReply: messageData.metadata?.autoReply || false
        },

      };

      const docRef = await addDoc(collection(db, this.messagesCollection), messageWithDefaults);
      
      // Update conversation
      await this.updateConversation(messageData.conversation.conversationId, {
        lastMessage: {
          content: messageData.conversation.content,
          senderId: messageData.conversation.senderId,
          timestamp: now
        },
        updatedAt: now
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating message:', error);
      throw error;
    }
  }

  // Get a single message by ID
  async getMessage(id: string): Promise<Message | null> {
    try {
      const docRef = doc(db, this.messagesCollection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Message;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting message:', error);
      throw error;
    }
  }

  // Get messages by conversation ID
  async getMessagesByConversation(conversationId: string, limitCount: number = 50): Promise<Message[]> {
    try {
      const q = query(
        collection(db, this.messagesCollection),
        where('conversation.conversationId', '==', conversationId),
        orderBy('conversation.createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];
      
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() } as Message);
      });

      return messages.reverse(); // Return in chronological order
    } catch (error) {
      console.error('Error getting messages by conversation:', error);
      throw error;
    }
  }

  // Get unread messages for a user
  async getUnreadMessages(userId: string): Promise<Message[]> {
    try {
      const q = query(
        collection(db, this.messagesCollection),
        where('conversation.recipientId', '==', userId),
        where('conversation.read', '==', false),
        orderBy('conversation.createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];
      
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() } as Message);
      });

      return messages;
    } catch (error) {
      console.error('Error getting unread messages:', error);
      throw error;
    }
  }

  // Mark message as read
  async markMessageAsRead(messageId: string): Promise<void> {
    try {
      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, {
        'conversation.read': true,
        'conversation.readAt': Timestamp.now(),
        'conversation.updatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  // Update a message
  async updateMessage(id: string, updates: Partial<Message>): Promise<void> {
    try {
      const docRef = doc(db, this.messagesCollection, id);
      const updateData = {
        ...updates,
        'conversation.updatedAt': Timestamp.now()
      };
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating message:', error);
      throw error;
    }
  }

  // Delete a message
  async deleteMessage(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.messagesCollection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  // Create a new conversation
  async createConversation(conversationData: Omit<Conversation, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const conversationWithDefaults: Omit<Conversation, 'id'> = {
        ...conversationData,
        createdAt: now,
        updatedAt: now,
        status: 'active'
      };

      const docRef = await addDoc(collection(db, this.conversationsCollection), conversationWithDefaults);
      return docRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  // Get a single conversation by ID
  async getConversation(id: string): Promise<Conversation | null> {
    try {
      const docRef = doc(db, this.conversationsCollection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Conversation;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }

  // Get conversations for a user
  async getConversationsForUser(userId: string): Promise<Conversation[]> {
    try {
      // Temporary workaround: Remove ordering until index is built
      const q = query(
        collection(db, this.conversationsCollection),
        where('participants', 'array-contains', userId)
      );

      const querySnapshot = await getDocs(q);
      const conversations: Conversation[] = [];
      
      querySnapshot.forEach((doc) => {
        conversations.push({ id: doc.id, ...doc.data() } as Conversation);
      });

      // Sort in memory instead
      conversations.sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || 0;
        const bTime = b.updatedAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order
      });

      return conversations;
    } catch (error) {
      console.error('Error getting conversations for user:', error);
      throw error;
    }
  }

  // Get all conversations (for admin)
  async getAllConversations(): Promise<Conversation[]> {
    try {
      const q = query(
        collection(db, this.conversationsCollection),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const conversations: Conversation[] = [];
      
      querySnapshot.forEach((doc) => {
        conversations.push({ id: doc.id, ...doc.data() } as Conversation);
      });

      return conversations;
    } catch (error) {
      console.error('Error getting all conversations:', error);
      throw error;
    }
  }

  // Update a conversation
  async updateConversation(id: string, updates: Partial<Conversation>): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, id);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating conversation:', error);
      throw error;
    }
  }

  // Archive a conversation
  async archiveConversation(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, id);
      await updateDoc(docRef, {
        status: 'archived',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  }

  // Close a conversation
  async closeConversation(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, id);
      await updateDoc(docRef, {
        status: 'closed',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error closing conversation:', error);
      throw error;
    }
  }

  // Get conversation by business and participants
  async getConversationByBusinessAndParticipants(businessId: string, participants: string[]): Promise<Conversation | null> {
    try {
      const q = query(
        collection(db, this.conversationsCollection),
        where('businessId', '==', businessId),
        where('participants', 'array-contains-any', participants)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as Conversation;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting conversation by business and participants:', error);
      throw error;
    }
  }

  // Send inquiry message
  async sendInquiry(
    businessId: string, 
    senderId: string, 
    recipientId: string, 
    subject: string, 
    inquiryData: {
      name: string;
      email: string;
      phone: string;
      message: string;
      username?: string;
      businessInfo?: {
        title: string;
        url: string;
        askingPrice: number;
        firstImageUrl?: string;
      };
    }
  ): Promise<string> {
    try {
      // Check if conversation exists
      let conversation = await this.getConversationByBusinessAndParticipants(businessId, [senderId, recipientId]);
      
      if (!conversation) {
        // Create new conversation
        const conversationId = await this.createConversation({
          participants: [senderId, recipientId],
          businessId,
          status: 'active',
          lastMessage: {
            content: inquiryData.message,
            senderId,
            timestamp: Timestamp.now()
          }
        });
        conversation = await this.getConversation(conversationId);
      }

      if (!conversation) {
        throw new Error('Failed to create or retrieve conversation');
      }

      // Create message in main messages collection (for conversation threading)
      const messageId = await this.createMessage({
        conversation: {
          conversationId: conversation.id!,
          businessId,
          senderId,
          recipientId,
          messageType: 'inquiry',
          subject,
          content: inquiryData.message, // Just the message content
          inquiryData, // Store structured data separately
          attachments: [],
          read: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        },
        metadata: {
          priority: 'normal',
          tags: ['inquiry'],
          autoReply: false
        }
      });

      // Also save to business subcollection for easy querying by business owner
      // This is a secondary write, so we don't fail if it errors
      try {
        await this.saveLeadToBusinessSubcollection(businessId, {
          conversationId: conversation.id!,
          senderId,
          recipientId,
          messageId,
          messageType: 'inquiry' as const,
          subject,
          inquiryData,
          read: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } catch (subcollectionError) {
        // Log but don't fail - main message is already saved
        console.warn('Failed to save lead to business subcollection (non-critical):', subcollectionError);
      }

      return messageId;
    } catch (error) {
      console.error('Error sending inquiry:', error);
      throw error;
    }
  }

  // Save lead to business subcollection
  private async saveLeadToBusinessSubcollection(
    businessId: string,
    leadData: {
      conversationId: string;
      senderId: string;
      recipientId: string;
      messageId: string;
      messageType: 'inquiry' | 'response' | 'follow_up' | 'system';
      subject: string;
      inquiryData: {
        name: string;
        email: string;
        phone: string;
        message: string;
        username?: string;
        businessInfo?: {
          title: string;
          url: string;
          askingPrice: number;
          firstImageUrl?: string;
        };
      };
      read: boolean;
      createdAt: Timestamp;
      updatedAt: Timestamp;
    }
  ): Promise<string> {
    const leadsRef = collection(db, 'businesses', businessId, 'leads');
    const docRef = await addDoc(leadsRef, leadData);
    return docRef.id;
  }

  // Get leads for a specific business (from subcollection)
  async getLeadsByBusiness(businessId: string): Promise<Array<{
    id: string;
    conversationId: string;
    senderId: string;
    recipientId: string;
    messageId: string;
    messageType: string;
    subject: string;
    inquiryData: {
      name: string;
      email: string;
      phone: string;
      message: string;
      username?: string;
      businessInfo?: {
        title: string;
        url: string;
        askingPrice: number;
        firstImageUrl?: string;
      };
    };
    read: boolean;
    createdAt: Timestamp;
    updatedAt: Timestamp;
  }>> {
    try {
      const leadsRef = collection(db, 'businesses', businessId, 'leads');
      const q = query(
        leadsRef,
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const leads: any[] = [];
      
      querySnapshot.forEach((doc) => {
        leads.push({ id: doc.id, ...doc.data() });
      });

      return leads;
    } catch (error) {
      console.error('Error getting leads by business:', error);
      throw error;
    }
  }

  // Get all leads for a business owner (across all their businesses)
  async getLeadsForBusinessOwner(ownerId: string): Promise<Array<{
    businessId: string;
    businessTitle?: string;
    leads: Array<{
      id: string;
      conversationId: string;
      senderId: string;
      inquiryData: any;
      createdAt: Timestamp;
      read: boolean;
    }>;
  }>> {
    try {
      // Get all businesses owned by this user
      const businesses = await businessService.getBusinessesByOwner(ownerId);
      
      // Get leads for each business
      const leadsByBusiness = await Promise.all(
        businesses.map(async (business) => {
          try {
            const leads = await this.getLeadsByBusiness(business.id!);
            return {
              businessId: business.id!,
              businessTitle: business.basic.title,
              leads: leads.map(lead => ({
                id: lead.id,
                conversationId: lead.conversationId,
                senderId: lead.senderId,
                inquiryData: lead.inquiryData,
                createdAt: lead.createdAt,
                read: lead.read
              }))
            };
          } catch (error) {
            console.error(`Error loading leads for business ${business.id}:`, error);
            return {
              businessId: business.id!,
              businessTitle: business.basic.title,
              leads: []
            };
          }
        })
      );

      // Filter out businesses with no leads and sort by most recent lead
      return leadsByBusiness
        .filter(b => b.leads.length > 0)
        .sort((a, b) => {
          const aTime = a.leads[0]?.createdAt?.toMillis?.() || 0;
          const bTime = b.leads[0]?.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
    } catch (error) {
      console.error('Error getting leads for business owner:', error);
      throw error;
    }
  }

  // Get message statistics for a user
  async getMessageStats(userId: string): Promise<{
    totalMessages: number;
    unreadMessages: number;
    activeConversations: number;
    avgResponseTime: number;
  }> {
    try {
      // Get user's conversations
      const conversations = await this.getConversationsForUser(userId);
      const activeConversations = conversations.filter(c => c.status === 'active').length;

      // Get unread messages
      const unreadMessages = await this.getUnreadMessages(userId);
      const unreadCount = unreadMessages.length;

      // Calculate average response time
      let totalResponseTime = 0;
      let responseCount = 0;

      for (const conversation of conversations) {
        const messages = await this.getMessagesByConversation(conversation.id!);
        for (let i = 1; i < messages.length; i++) {
          const currentMessage = messages[i];
          const previousMessage = messages[i - 1];
          
          if (currentMessage.conversation.senderId !== previousMessage.conversation.senderId) {
            const responseTime = currentMessage.conversation.createdAt.toMillis() - previousMessage.conversation.createdAt.toMillis();
            totalResponseTime += responseTime;
            responseCount++;
          }
        }
      }

      const avgResponseTime = responseCount > 0 ? totalResponseTime / responseCount : 0;

      return {
        totalMessages: conversations.length,
        unreadMessages: unreadCount,
        activeConversations,
        avgResponseTime
      };
    } catch (error) {
      console.error('Error getting message stats:', error);
      throw error;
    }
  }
}

export const messageService = new MessageService();
export default messageService; 